import { FC, ReactNode, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import customerStore from "features/organizations/customers/store/customer-store"
import { Button, FormattedMessage, PageTitle, Table, ToastLevel, useIntl, useToast } from "@metsooutotec/modes-react-components"
import { assetCellsStyle } from "../../common/styles/customer-styles"
import EmptyImage from "imgs/emptyImage.jpg"
import ListView from "../../../../components/utils/list-view/list-view"
import { NotFound } from "../../../../components/utils/not-found"
import { ModalTypes } from "../../customers/utils/customer-modal-helper"
import AssetModal from "../components/asset-modal"
import EntityDeleteModal from "../../customers/components/entity-delete-modal"
import { NavigationDeleteEntityOptions, NavigationModalOptions } from "../../../../components/utils/modal-helper"
import { GenerateBreadcrumbs } from "../../customers/utils/customers-bread-crumbs"
import { downloadFileFromBlobUrl } from "../../../../components/utils/files-utils"
import {
    itemViewDetailsContainerStyles,
    itemViewDetailsTableStyles,
    itemViewListContainerStyles,
    itemViewListExtraTopPaddingStyles,
    itemViewSeparatorStyles,
    itemViewSubtitleStyles,
} from "../../../../components/common-style/common-style"
import LowSpinner from "../../../../components/common/low-spinner"
import assetSectionFilesStore from "../../asset-sections/store/asset-section-files-store"
import { AssetSection } from "features/organizations/asset-sections/models/asset-section-models"
import { getAsset3DFile, getAssetImage } from "../services/asset-services"
import AssetSectionModal from "features/organizations/asset-sections/pages/view/asset-section-modal"

const AssetView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { customerId, siteId, assetId } = useParams()
    const { setMessage } = useToast()
    const location = useLocation()
    const assetSectionStore = assetSectionFilesStore
    const params: NavigationModalOptions = location.state
    const { customerMap, siteMap, assetMap } = customerStore
    const customer = customerMap[customerId || ""]
    const site = siteMap[siteId || ""]
    const asset = assetMap[assetId || ""]
    const [isAssetModalOpen, setAssetModalOpen] = useState(false)
    const [loadingAssetImage, setLoadingAssetImage] = useState(true)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [isAssetSectionModalOpen, setAssetSectionModalOpen] = useState(false)
    const [assetImage, setAssetImage] = useState(EmptyImage)
    const breadcrumbsPath = GenerateBreadcrumbs(customer, site, asset, undefined)
    const [downloadLoading, setDownloadLoading] = useState<boolean>(false)

    const template = [
        {
            headingKey: "assetSection.name",
            cellFunc: (a: AssetSection) => a.name,
        },
        {
            headingKey: "assetSection.description",
            cellFunc: (a: AssetSection) => a.description,
        },
        {
            headingKey: "assetSection.SvgFile",
            cellFunc: (a: AssetSection) => {
                if (assetSectionStore.isAnySectionLoading) {
                    return "Loading..."
                } else {
                    // Check if the SVG file exists in the assetSectionFilesStore
                    const checkFilesExist = assetSectionFilesStore.assetSectionFilesMap[a.id]
                    return checkFilesExist && checkFilesExist.isSvgExist ? `${a.name}.svg` : "Not Exist"
                }
            },
        },
        {
            headingKey: "assetSection.MudMap",
            cellFunc: (a: AssetSection) => {
                if (assetSectionStore.isAnySectionLoading) {
                    return "Loading..."
                } else {
                    // Check if the Mud Map PDF file exists in the assetSectionFilesStore
                    const checkFilesExist = assetSectionFilesStore.assetSectionFilesMap[a.id]
                    return checkFilesExist && checkFilesExist.isPdfExist ? `${a.name}Mudmap.pdf` : "Not Exist"
                }
            },
        },
    ]
    const showNotifier = (text: string) => {
        setMessage(text, ToastLevel.SUCCESS, 6000)
    }
    const fetchImage = async () => {
        try {
            setLoadingAssetImage(true)
            const imageUrl = await getAssetImage(assetId as string)
            setAssetImage(imageUrl)
            setLoadingAssetImage(false)
        } catch {
            setLoadingAssetImage(false)
        }
    }

    const checkFilesExists = async () => {
        try {
            asset.assetSections.forEach(async a => {
                console.log(a.id)
                await assetSectionStore.loadassetSectionFiles(a.id)
            })
        } catch (e) {
            console.warn("Error in checkFilesExists", e)
        }
    }

    useEffect(() => {
        checkFilesExists()
    }, [assetId])

    const memoizedFetchImage = useMemo(() => fetchImage, [assetId, params])

    useEffect(() => {
        memoizedFetchImage()
    }, [memoizedFetchImage])

    useEffect(() => {
        if (params?.type === "created") {
            showNotifier("New Asset created successfully")
        }
        if (params?.type === "edited") {
            showNotifier("All changes made successfully")
        }
        window.history.replaceState({}, document.title)
    }, [params])

    const editAssetModal = {
        open: () => {
            setAssetModalOpen(true)
        },
        close: (passed: boolean, assetId?: string, navigationCustomerOptions?: NavigationModalOptions) => {
            setAssetModalOpen(false)
            if (passed) {
                navigate(location.pathname, {
                    state: navigationCustomerOptions,
                })
            }
        },
    }

    const deleteAssetModal = {
        open: () => {
            setIsDeleteModalOpen(true)
        },
        close: (params: NavigationDeleteEntityOptions) => {
            setIsDeleteModalOpen(false)
            if (params.deleted) {
                navigate(`/customers/${customerId}/sites/${siteId}`, { state: params })
            }
        },
    }

    const createAssetSectionModal = {
        open: () => {
            setAssetSectionModalOpen(true)
        },
        close: (passed: boolean, assetSectionId?: string, navigationCustomerOptions?: NavigationModalOptions) => {
            setAssetSectionModalOpen(false)
            if (passed) {
                navigate(`${location.pathname}/assetsections/${assetSectionId}`, {
                    state: navigationCustomerOptions,
                })
            }
        },
    }

    const download3DFile = () => {
        setDownloadLoading(true)
        getAsset3DFile(assetId as string)
            .then(blobUrl => {
                if (blobUrl) {
                    downloadFileFromBlobUrl(blobUrl, `Asset_${assetId}-3D.obj`)
                } else {
                    setMessage("The requested file could not be found", ToastLevel.WARNING, 6000)
                }
            })
            .catch(err => {
                console.error("An error occurred:", err)
                setMessage("An error occurred while downloading the file", ToastLevel.ALERT, 6000)
            })
            .finally(() => {
                setDownloadLoading(false)
            })
    }
    const rightPageButtons: ReactNode = (
        <>
            <Button
                icon='icon-download-16'
                labelPosition='right'
                onClick={download3DFile}
                primary
                loading={downloadLoading}
                disabled={downloadLoading}
            >
                {formatMessage("asset.download3DFile")}
            </Button>
            <Button icon='icon-edit-16' labelPosition='right' onClick={editAssetModal.open} primary>
                {formatMessage("asset.edit")}
            </Button>
            <Button icon='icon-trash-16' labelPosition='left' onClick={deleteAssetModal.open} secondary>
                {formatMessage("asset.delete")}
            </Button>
        </>
    )
    const rightAssetSectionButton: ReactNode = (
        <>
            <Button icon='icon-plus-16' labelPosition='right' onClick={createAssetSectionModal.open} primary>
                {formatMessage("assetSection.addNew")}
            </Button>
        </>
    )
    const navStub = `/customers/${customer?.id}/sites/${site?.id}/assets/${asset?.id}/assetsections`

    return !asset ? (
        <NotFound id={assetId} />
    ) : (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={asset.name} extraItems={rightPageButtons} />
            <div style={itemViewDetailsContainerStyles}>
                <h3 style={itemViewSubtitleStyles}>
                    <FormattedMessage id='asset.details' />
                </h3>
                <div style={{ display: "flex", gap: "2rem" }}>
                    {/* Left column: Details */}
                    <div style={{ flex: 1 }}>
                        <Table style={itemViewDetailsTableStyles} tight>
                            <Table.Body>
                                <Table.Row>
                                    <Table.HeaderCell style={assetCellsStyle}>
                                        <FormattedMessage id='asset.description' />
                                    </Table.HeaderCell>
                                    <Table.Cell style={assetCellsStyle}>{asset.description || "Empty"}</Table.Cell>
                                </Table.Row>

                                <Table.Row>
                                    <Table.HeaderCell style={assetCellsStyle}>
                                        <FormattedMessage id='asset.wearThreshold' />
                                    </Table.HeaderCell>
                                    <Table.Cell style={assetCellsStyle}>{asset.wearThreshold || "Empty"}</Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        </Table>
                    </div>

                    {/* Right column: Image */}
                    <div style={{ width: "40%", textAlign: "right" }}>
                        {loadingAssetImage ? (
                            <LowSpinner id='asset.imageLoading' />
                        ) : (
                            <img src={assetImage} alt='logo' style={{ width: "100%", objectFit: "contain" }} />
                        )}
                    </div>
                </div>
            </div>
            <div style={itemViewSeparatorStyles}></div>
            <div style={itemViewListContainerStyles}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <h3 style={itemViewSubtitleStyles}>
                        <FormattedMessage id='assetSectionList.title' />
                    </h3>
                    {rightAssetSectionButton}
                </div>
                <div style={asset.assetSections && asset.assetSections.length ? itemViewListExtraTopPaddingStyles : {}}>
                    <ListView
                        data={asset.assetSections?.slice().sort((a, b) => a?.name?.localeCompare(b?.name)) || []}
                        noneYetKey='assetSectionList.noAssetSectionYet'
                        onClickRow={(a: AssetSection) => navigate(`${navStub}/${a.id}`)}
                        template={template}
                    />
                </div>
            </div>
            <AssetModal isOpen={isAssetModalOpen} onClose={editAssetModal.close} asset={asset} siteId={siteId} customerId={customerId} />
            <AssetSectionModal
                isOpen={isAssetSectionModalOpen}
                onClose={createAssetSectionModal.close}
                assetId={assetId}
                siteId={siteId}
                customerId={customerId}
            />
            <EntityDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={deleteAssetModal.close}
                entity={asset}
                modalType={ModalTypes.AssetModal}
                customerId={customer?.id}
                siteId={siteId}
            />
        </>
    )
})

export default AssetView
